import { readLocationLabelFromObject } from '@/utils/orderSurveyDisplay'

/**
 * Read-only display of a beneficiary's onboarding survey answers.
 *
 * `surveyData` arrives from GraphQL as a JSON-encoded string that parses to a flat
 * `Record<string, unknown>` (question code -> answer value). The full object can contain
 * arbitrary, untyped keys — including PII — so we surface only an allowlist of codes, drop a
 * PII blocklist, and defensively format every possible value kind.
 *
 * Mirrors the label-map convention in `surveyQuestionDisplayNames.ts`.
 */

export interface BeneficiarySurveyRow {
  code: string
  label: string
  valueText: string
}

type Locale = 'en' | 'ar'

/** Allowlisted codes with localized labels. Iteration order = display order within a group. */
export const BENEFICIARY_SURVEY_LABELS: Record<string, { en: string; ar: string }> = {
  beneficiary_care_needs: { en: 'Care needs', ar: 'احتياجات الرعاية' },
  beneficiary_mobility: { en: 'Mobility / care level', ar: 'الحركة / مستوى الرعاية' },
  beneficiary_age_group: { en: 'Age group', ar: 'الفئة العمرية' },
  beneficiary_special_requirements: { en: 'Special requirements', ar: 'متطلبات خاصة' },
  are_you_a_person_with_special_needs: {
    en: 'Person with special needs?',
    ar: 'هل يوجد لدى المستفيد إعاقات؟',
  },
  HC11: { en: 'Is the patient bedridden?', ar: 'هل المستفيد طريح الفراش؟' },

  beneficiary_medical_conditions: {
    en: 'Medical details (survey)',
    ar: 'تفاصيل طبية (الاستبيان)',
  },
  HC13: { en: 'The patient has the following conditions', ar: 'لدى المستفيد الحالات التالية' },
  do_you_have_any_chronic_diseases: {
    en: 'Any chronic diseases?',
    ar: 'هل يوجد لدى المستفيد أمراض مزمنة؟',
  },
  ALG1: {
    en: 'Any medication allergies?',
    ar: 'هل يوجد لدى المستفيد حساسية من أي دواء؟',
  },
  do_have_any_known_allergies: {
    en: 'Any known allergies?',
    ar: 'هل يوجد لدى المستفيد حساسية من أي دواء؟',
  },
  MED6: {
    en: 'Taking prescribed medications requiring medical supervision?',
    ar: 'هل يتناول المستفيد أدوية موصوفة تحتاج إلى إشراف طبي؟',
  },
  are_you_currently_taking_any_medications: {
    en: 'Currently taking any medications?',
    ar: 'هل يتناول المستفيد أدوية موصوفة تحتاج إلى إشراف طبي؟',
  },

  VS5: { en: 'Weight', ar: 'الوزن' },
  VS6: { en: 'Height', ar: 'الطول' },
}

/**
 * Codes that must never be displayed even if present, matched case-insensitively as a
 * substring of the key (so e.g. `national_id_iqama_number` and `patient_phone` both match).
 */
const PII_BLOCKLIST_FRAGMENTS = [
  'national_id',
  'iqama',
  'phone',
  'mobile',
  'email',
  'full_name',
  'fullname',
  'first_name',
  'last_name',
  'date_of_birth',
  'dob',
  'birth',
  'gender',
]

export interface BeneficiarySurveyGroup {
  /** i18n key under `orderDetails` for the group heading. */
  titleKey: string
  codes: string[]
}

/** Ordered grouping used by the Health & Care Profile card. */
export const BENEFICIARY_SURVEY_GROUPS: BeneficiarySurveyGroup[] = [
  {
    titleKey: 'healthProfileGroupCare',
    codes: [
      'beneficiary_care_needs',
      'beneficiary_mobility',
      'beneficiary_age_group',
      'beneficiary_special_requirements',
      'are_you_a_person_with_special_needs',
      'HC11',
    ],
  },
  {
    titleKey: 'healthProfileGroupMedical',
    codes: [
      'beneficiary_medical_conditions',
      'HC13',
      'do_you_have_any_chronic_diseases',
      'ALG1',
      'do_have_any_known_allergies',
      'MED6',
      'are_you_currently_taking_any_medications',
    ],
  },
  {
    titleKey: 'healthProfileGroupVitals',
    codes: ['VS5', 'VS6'],
  },
]

function isPiiCode(code: string): boolean {
  const lower = code.toLowerCase()
  return PII_BLOCKLIST_FRAGMENTS.some((fragment) => lower.includes(fragment))
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value == null || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

const EMPTY_STRINGS = new Set(['', '[]', '{}', 'null', 'undefined'])

/** ISO-ish date strings that are safe to localize. */
function formatMaybeDate(value: string, locale: Locale): string {
  if (!/^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/.test(value.trim())) return value
  const ts = Date.parse(value)
  if (Number.isNaN(ts)) return value
  return new Date(ts).toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatScalar(value: string | number | boolean, locale: Locale): string {
  if (typeof value === 'boolean') {
    if (locale === 'ar') return value ? 'نعم' : 'لا'
    return value ? 'Yes' : 'No'
  }
  if (typeof value === 'number') return String(value)
  const lower = value.trim().toLowerCase()
  if (lower === 'true' || lower === 'yes') return locale === 'ar' ? 'نعم' : 'Yes'
  if (lower === 'false' || lower === 'no') return locale === 'ar' ? 'لا' : 'No'
  return formatMaybeDate(value, locale)
}

/** Defends against every value kind the untyped survey object may hold. Returns '' to skip. */
function formatValueForRow(value: unknown, locale: Locale): string {
  if (value == null) return ''

  if (Array.isArray(value)) {
    const parts = value
      .map((item) =>
        typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
          ? formatScalar(item, locale)
          : ''
      )
      .map((part) => part.trim())
      .filter(Boolean)
    return parts.join(locale === 'ar' ? '، ' : ', ')
  }

  if (typeof value === 'object') {
    const obj = asRecord(value)
    if (!obj) return ''
    const address = readLocationLabelFromObject(obj)
    if (address) return address
    const json = JSON.stringify(obj)
    return json.length > 200 ? `${json.slice(0, 200)}…` : json
  }

  if (typeof value === 'string') {
    if (EMPTY_STRINGS.has(value.trim().toLowerCase())) return ''
    return formatScalar(value, locale)
  }

  return formatScalar(value as number | boolean, locale)
}

/**
 * Parse `surveyData` and return display rows, in allowlist order, scrubbed of PII and empties.
 * Returns `[]` on empty / malformed input (never throws).
 */
export function getBeneficiarySurveyRows(
  surveyData: string | null | undefined,
  locale: Locale
): BeneficiarySurveyRow[] {
  if (!surveyData || typeof surveyData !== 'string' || !surveyData.trim()) return []

  let parsed: Record<string, unknown> | null
  try {
    parsed = asRecord(JSON.parse(surveyData))
  } catch {
    return []
  }
  if (!parsed) return []

  const rows: BeneficiarySurveyRow[] = []
  for (const code of Object.keys(BENEFICIARY_SURVEY_LABELS)) {
    if (isPiiCode(code)) continue
    if (!(code in parsed)) continue
    const valueText = formatValueForRow(parsed[code], locale)
    if (!valueText) continue
    rows.push({
      code,
      label: locale === 'ar' ? BENEFICIARY_SURVEY_LABELS[code].ar : BENEFICIARY_SURVEY_LABELS[code].en,
      valueText,
    })
  }
  return rows
}
